import React, { useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import api from '../../services/api';
import { marketingPalette } from '../../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../common/PublicPageFrame';

const palette = marketingPalette;

const FeedbackDialog = ({ open, onClose, caseId = null, historyId = null }) => {
  const [formData, setFormData] = useState({
    feedbackType: '',
    rating: 3,
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const feedbackTypes = [
    { value: 'usability', label: 'Usability Issue' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'case_specific', label: 'Case-Specific Feedback' },
    { value: 'general', label: 'General Feedback' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Get browser information
      const browserInfo = {
        name: navigator.userAgent,
        version: navigator.appVersion,
        os: navigator.platform
      };

      await api.post('/feedback', {
        ...formData,
        caseId,
        historyId,
        browserInfo
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      feedbackType: '',
      rating: 3,
      subject: '',
      message: ''
    });
    setSuccess(false);
    setError('');
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
      resetForm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          border: `1px solid ${alpha(palette.ink, 0.08)}`,
          boxShadow: '0 30px 80px rgba(18, 61, 103, 0.14)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${alpha(palette.ink, 0.08)}`,
          fontFamily: publicHeadingFontFamily,
          fontSize: { xs: '1.45rem', md: '1.65rem' },
          letterSpacing: '-0.03em',
          color: palette.ink,
        }}
      >
        Send feedback
      </DialogTitle>
      <DialogContent sx={{ py: 2.5, bgcolor: alpha(palette.heroBlueMist, 0.36) }}>
        {success ? (
          <Alert severity="success" sx={{ mt: 1, borderRadius: '18px', fontFamily: publicBodyFontFamily }}>
            Thank you for your feedback! It helps us improve the platform.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <FormControl
              fullWidth
              margin="normal"
              required
              sx={{
                '& .MuiInputLabel-root': {
                  color: palette.inkSoft,
                  fontFamily: publicBodyFontFamily,
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '18px',
                  bgcolor: alpha('#ffffff', 0.96),
                  fontFamily: publicBodyFontFamily,
                  '& fieldset': {
                    borderColor: alpha(palette.ink, 0.10),
                  },
                },
              }}
            >
              <InputLabel>Feedback type</InputLabel>
              <Select
                value={formData.feedbackType}
                onChange={(e) => setFormData({ ...formData, feedbackType: e.target.value })}
                label="Feedback type"
              >
                {feedbackTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography component="legend" sx={{ color: palette.ink, fontWeight: 700, fontFamily: publicBodyFontFamily }}>
                Overall rating
              </Typography>
              <Rating
                value={formData.rating}
                onChange={(event, newValue) => 
                  setFormData({ ...formData, rating: newValue || 1 })
                }
                size="large"
                sx={{ color: palette.heroBlueMid, mt: 0.5 }}
              />
            </Box>

            <TextField
              fullWidth
              margin="normal"
              label="Subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
              inputProps={{ maxLength: 200 }}
              helperText={`${formData.subject.length}/200 characters`}
              sx={publicTextFieldSx}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Message"
              multiline
              rows={6}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              inputProps={{ maxLength: 2000 }}
              helperText={`${formData.message.length}/2000 characters`}
              sx={publicTextFieldSx}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '18px', fontFamily: publicBodyFontFamily }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, bgcolor: alpha(palette.heroBlueMist, 0.36) }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ ...publicSecondaryButtonSx, minHeight: 44 }}>
          Cancel
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            disabled={submitting || !formData.feedbackType || !formData.subject || !formData.message}
            startIcon={submitting && <CircularProgress size={20} />}
            sx={{ ...publicPrimaryButtonSx, minHeight: 44 }}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
